import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Coins, AlertTriangle, Check, Loader2, ShoppingCart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";

/**
 * CreditCheckDialog — Shows before batch AI operations.
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   onConfirm: () => void  — called when user confirms to proceed
 *   restaurantId: string
 *   action: "image_gen_dish" | "recipe_gen"
 *   count: number — how many items to generate
 *   actionLabel: string — e.g. "imagenes" or "recetas"
 */
export default function CreditCheckDialog({ open, onClose, onConfirm, restaurantId, action, count, actionLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !restaurantId || !count) return;
    setLoading(true);
    api.request(`/restaurants/${restaurantId}/ai/credit-precheck`, {
      method: "POST",
      body: JSON.stringify({ action, count }),
    })
      .then(setData)
      .catch(() => setData({ enough: true, error: true }))
      .finally(() => setLoading(false));
  }, [open, restaurantId, action, count]);

  if (!open) return null;

  const enough = data?.enough;
  const affordable = data?.affordable || 0;
  const shortfall = data?.shortfall || 0;
  const noSub = data?.has_subscription === false;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="credit-check-dialog">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

        {/* Header */}
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-base font-bold flex items-center gap-2">
            <Coins className="w-5 h-5 text-primary" /> Verificacion de creditos
          </h2>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : noSub ? (
            /* No subscription — proceed freely */
            <div className="text-center py-4 space-y-3">
              <Check className="w-10 h-10 text-emerald-500 mx-auto" />
              <p className="text-sm">Generar <span className="font-bold">{count} {actionLabel}</span></p>
              <p className="text-xs text-muted-foreground">Sin suscripcion activa — creditos ilimitados en modo trial</p>
            </div>
          ) : enough ? (
            /* Enough credits */
            <div className="space-y-4">
              <div className="text-center py-2">
                <Check className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Creditos suficientes</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Operacion</span>
                  <span className="font-medium">{count} {actionLabel}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Coste por unidad</span>
                  <span>{data.cost_per} credito{data.cost_per !== 1 ? "s" : ""}</span>
                </div>
                <div className="flex justify-between border-t border-border pt-2">
                  <span className="font-semibold">Coste total</span>
                  <span className="font-bold text-primary">{data.total_cost} creditos</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Creditos disponibles</span>
                  <span className="text-emerald-500 font-medium">{data.remaining}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Despues de la operacion</span>
                  <span>{data.remaining - data.total_cost}</span>
                </div>
              </div>
            </div>
          ) : (
            /* Not enough credits */
            <div className="space-y-4">
              <div className="text-center py-2">
                <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Creditos insuficientes</p>
              </div>
              <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Necesitas</span>
                  <span className="font-bold text-red-500">{data.total_cost} creditos</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tienes</span>
                  <span className="font-medium">{data.remaining} creditos</span>
                </div>
                <div className="flex justify-between border-t border-border pt-2">
                  <span className="text-muted-foreground">Te faltan</span>
                  <span className="font-bold text-red-500">{shortfall} creditos</span>
                </div>
              </div>
              {affordable > 0 && (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-700">
                  Puedes generar <span className="font-bold">{affordable}</span> de {count} {actionLabel} con tus creditos actuales
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border flex items-center justify-between gap-2">
          <Button variant="outline" onClick={onClose} size="sm">Cancelar</Button>
          <div className="flex gap-2">
            {!loading && !enough && !noSub && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { onClose(); navigate("/app/subscription"); }}
                data-testid="buy-credits-btn"
                className="gap-1.5"
              >
                <ShoppingCart className="w-3.5 h-3.5" /> Comprar creditos
              </Button>
            )}
            {!loading && (enough || noSub) && (
              <Button size="sm" onClick={onConfirm} data-testid="confirm-generate-btn" className="gap-1.5">
                <Check className="w-3.5 h-3.5" /> Generar {count} {actionLabel}
              </Button>
            )}
            {!loading && !enough && !noSub && affordable > 0 && (
              <Button size="sm" variant="secondary" onClick={onConfirm} data-testid="confirm-partial-btn" className="gap-1.5">
                Generar {affordable} {actionLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
